import React, { useEffect, useState } from 'react';
import axiosInstance from '../axios';
import toast from 'react-hot-toast';
import { MdOutlineGroups, MdOutlinePerson, MdOutlineEmail } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../redux/hooks';
import { isAdmin } from '../redux/types/authTypes';
import { Navigate } from 'react-router-dom';
import ComponentWrapper from '../components/utils/ComponentWrapper';

interface ManagerData {
  id: string;
  employeeId: number;
  fullname: string;
  email: string;
  profilePicture: string;
  teamMemberCount: number;
  designation: {
    id: string;
    name: string;
  };
}

interface TeamMember {
  id: string;
  employeeId: number;
  name: string;
  email: string;
  designation: string;
  joinDate: string;
  status: string;
  jobLocation: string;
}

const ManagerOverview: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.auth);

  const [managers, setManagers] = useState<ManagerData[]>([]);
  const [loadingManagers, setLoadingManagers] = useState(true);
  const [selectedManager, setSelectedManager] = useState<ManagerData | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  if (!isAdmin(user)) return <Navigate to="/dashboard" replace />;

  useEffect(() => {
    const fetchManagers = async () => {
      setLoadingManagers(true);
      try {
        const res = await axiosInstance.get('/v1/admin/manager-employees');
        if (res.data?.success) setManagers(res.data.data);
        else toast.error('Failed to load managers');
      } catch {
        toast.error('Failed to load managers');
      } finally {
        setLoadingManagers(false);
      }
    };
    fetchManagers();
  }, []);

  const loadTeam = async (manager: ManagerData) => {
    setSelectedManager(manager);
    setTeamMembers([]);
    setLoadingTeam(true);
    try {
      const res = await axiosInstance.get(`/v1/admin/team-members/${manager.id}`);
      if (res.data?.success) setTeamMembers(res.data.data);
      else toast.error('Failed to load team members');
    } catch {
      toast.error('Failed to load team members');
    } finally {
      setLoadingTeam(false);
    }
  };

  const filteredManagers = managers.filter(m =>
    m.fullname.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.designation.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <ComponentWrapper
      icon={<MdOutlineGroups size={28} />}
      title="Manager Overview"
      buttonOne={
        <span className="text-sm text-gray-500">
          {managers.length} manager{managers.length !== 1 ? 's' : ''} total
        </span>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Managers List */}
        <div className="flex flex-col gap-4">
          {/* Search */}
          <input
            type="text"
            placeholder="Search managers..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
          />

          {loadingManagers ? (
            <div className="flex justify-center items-center h-32 text-gray-400 text-sm">
              Loading managers...
            </div>
          ) : filteredManagers.length === 0 ? (
            <div className="text-center text-gray-400 py-8 text-sm">
              {searchQuery ? 'No managers match your search.' : 'No managers found.'}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredManagers.map(manager => (
                <button
                  key={manager.id}
                  onClick={() => loadTeam(manager)}
                  className={`w-full text-left flex items-center gap-4 p-4 rounded-2xl border transition-all ${
                    selectedManager?.id === manager.id
                      ? 'border-blue-400 bg-blue-50 shadow-sm'
                      : 'border-gray-100 bg-white hover:border-gray-300 hover:shadow-sm'
                  }`}
                >
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-semibold text-base flex-shrink-0 overflow-hidden">
                    {manager.profilePicture ? (
                      <img src={manager.profilePicture} alt={manager.fullname} className="w-full h-full object-cover" />
                    ) : (
                      manager.fullname.charAt(0).toUpperCase()
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-800 truncate">{manager.fullname}</div>
                    <div className="text-xs text-gray-500 truncate">{manager.designation.name}</div>
                    <div className="text-xs text-gray-400 truncate">{manager.email}</div>
                  </div>

                  {/* Team Count Badge */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${
                      manager.teamMemberCount > 0
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      <MdOutlineGroups className="text-base" />
                      {manager.teamMemberCount}
                    </div>
                    <span className="text-[10px] text-gray-400 mt-0.5">members</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Team Members Panel */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 min-h-[300px]">
          {!selectedManager ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-gray-400">
              <MdOutlineGroups className="text-5xl mb-2" />
              <p className="text-sm">Select a manager to see their team</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-semibold flex-shrink-0 overflow-hidden">
                  {selectedManager.profilePicture ? (
                    <img src={selectedManager.profilePicture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    selectedManager.fullname.charAt(0).toUpperCase()
                  )}
                </div>
                <div>
                  <div className="font-semibold text-gray-800">{selectedManager.fullname}</div>
                  <div className="text-xs text-gray-500">{selectedManager.designation.name}</div>
                </div>
                <div className="ml-auto text-sm text-gray-500">
                  {selectedManager.teamMemberCount} member{selectedManager.teamMemberCount !== 1 ? 's' : ''}
                </div>
              </div>

              {loadingTeam ? (
                <div className="flex justify-center py-8 text-gray-400 text-sm">Loading team...</div>
              ) : teamMembers.length === 0 ? (
                <div className="text-center text-gray-400 py-8 text-sm">
                  No team members assigned yet.
                </div>
              ) : (
                <div className="flex flex-col divide-y divide-gray-50">
                  {teamMembers.map(member => (
                    <div key={member.id} className="flex items-center gap-3 py-3">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-sm font-medium flex-shrink-0">
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800 truncate">{member.name}</div>
                        <div className="text-xs text-gray-400 truncate">{member.designation}</div>
                      </div>
                      <div className="flex-shrink-0 text-right">
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${
                          member.status === 'Active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`}>
                          {member.status}
                        </span>
                        <div className="text-xs text-gray-400 mt-0.5">{member.jobLocation}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </ComponentWrapper>
  );
};

export default ManagerOverview;
